import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Grid from '../common/Grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { apiEndpoints } from '../../config/api';
import {
  groupSaltsByCategory,
  classifySalt,
  SALT_CATEGORY_ORDER,
  type SaltCategory,
} from '../../utils/saltCategories';
import {
  resolveSolubility,
  resolveDensity,
  formatSolubility,
  formatDensity,
} from '../../utils/saltPhysicalData';
import {
  detectSaltPhysicalForm,
  physicalFormLabel,
  type SaltPhysicalForm,
} from '../../utils/saltPhysicalForm';
import { saltHasComposition } from '../../utils/saltElements';

interface SaltElement {
  id: string;
  percentage: number;
  element: {
    id: string;
    symbol: string;
    name: string;
  };
}

interface Salt {
  id: string;
  name: string;
  formula: string;
  isCustom: boolean;
  density?: number | null;
  solubility?: number | null;
  isLiquid?: boolean | null;
  elements: SaltElement[];
}

interface SaltLibraryReviewProps {
  onSaltSelection: (salts: Salt[]) => void;
  refreshTrigger?: number;
  /** Controlled selection (e.g. load named combination) */
  selectedIds?: string[];
  onSelectedIdsChange?: (ids: string[]) => void;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SaltLibraryReview: React.FC<SaltLibraryReviewProps> = ({
  onSaltSelection,
  refreshTrigger = 0,
  selectedIds,
  onSelectedIdsChange,
}) => {
  const [salts, setSalts] = useState<Salt[]>([]);
  const [filteredSalts, setFilteredSalts] = useState<Salt[]>([]);
  const [internalSelected, setInternalSelected] = useState<string[]>([]);
  const isControlled = selectedIds !== undefined;
  const selectedSalts = isControlled ? selectedIds! : internalSelected;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState('');

  // Confirm dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [elementFilter, setElementFilter] = useState('');
  const [formFilter, setFormFilter] = useState<'all' | SaltPhysicalForm>('all');
  const [customFilter, setCustomFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState<SaltCategory | 'all'>('all');
  /** Incomplete = no element % in DB ("Not entered") — hidden by default */
  const [showIncomplete, setShowIncomplete] = useState(false);

  const fetchSalts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${apiEndpoints.salts.list}?includeElements=true`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch salts');
      }

      const data = await response.json();
      setSalts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load salts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalts();
  }, [fetchSalts, refreshTrigger]);

  useEffect(() => {
    let filtered = [...salts];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (salt) =>
          salt.name.toLowerCase().includes(q) ||
          salt.formula?.toLowerCase().includes(q)
      );
    }

    if (elementFilter) {
      filtered = filtered.filter((salt) =>
        salt.elements?.some((el) => el.element?.symbol === elementFilter)
      );
    }

    if (formFilter !== 'all') {
      filtered = filtered.filter((salt) => detectSaltPhysicalForm(salt) === formFilter);
    }

    if (customFilter !== 'all') {
      filtered = filtered.filter((salt) =>
        customFilter === 'custom' ? salt.isCustom : !salt.isCustom
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter((salt) => classifySalt(salt) === categoryFilter);
    }

    if (!showIncomplete) {
      filtered = filtered.filter((salt) => saltHasComposition(salt));
    }

    setFilteredSalts(filtered);
  }, [
    salts,
    searchTerm,
    elementFilter,
    formFilter,
    customFilter,
    categoryFilter,
    showIncomplete,
  ]);

  const grouped = useMemo(() => groupSaltsByCategory(filteredSalts), [filteredSalts]);

  const syncSelection = (ids: string[], sourceList: Salt[] = salts) => {
    if (!isControlled) setInternalSelected(ids);
    onSelectedIdsChange?.(ids);
    onSaltSelection(sourceList.filter((s) => ids.includes(s.id)));
  };

  // When controlled ids change from parent (load combination), re-emit full salt objects
  useEffect(() => {
    if (!isControlled || !salts.length) return;
    onSaltSelection(salts.filter((s) => selectedSalts.includes(s.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when ids or catalog change
  }, [isControlled, selectedIds, salts]);

  const handleSelectGroup = (groupSaltIds: string[]) => {
    const completeIds = groupSaltIds.filter((id) => {
      const s = salts.find((x) => x.id === id);
      return s && saltHasComposition(s);
    });
    const merged = [...new Set([...selectedSalts, ...completeIds])];
    syncSelection(merged);
    setSnackbar(
      completeIds.length
        ? `Added ${completeIds.length} salt(s) from group to selection`
        : 'No complete salts in this group'
    );
  };

  const handleSaltToggle = (saltId: string) => {
    const salt = salts.find((s) => s.id === saltId);
    // Selecting incomplete composition only confuses coverage analysis
    if (salt && !saltHasComposition(salt) && !selectedSalts.includes(saltId)) {
      setSnackbar(
        `"${salt.name}" has no element % in the database (Not entered). Use the hydrated/complete twin, or delete this row.`
      );
      return;
    }
    const newSelection = selectedSalts.includes(saltId)
      ? selectedSalts.filter((id) => id !== saltId)
      : [...selectedSalts, saltId];
    syncSelection(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedSalts.length === filteredSalts.length && filteredSalts.length > 0) {
      syncSelection([]);
    } else {
      syncSelection(
        filteredSalts.map((s) => s.id),
        filteredSalts
      );
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setElementFilter('');
    setFormFilter('all');
    setCustomFilter('all');
    setCategoryFilter('all');
    setShowIncomplete(false);
  };

  const openDeleteConfirm = (ids: string[]) => {
    if (!ids.length) return;
    if (!localStorage.getItem('token')) {
      setSnackbar('Sign in to delete salts from the library.');
      return;
    }
    setPendingDeleteIds(ids);
    setConfirmOpen(true);
  };

  const handleDeleteOne = (saltId: string) => {
    openDeleteConfirm([saltId]);
  };

  const handleDeleteSelected = () => {
    openDeleteConfirm(selectedSalts);
  };

  const executeDelete = async () => {
    if (!pendingDeleteIds.length) return;
    setDeleting(true);
    setActionMessage(null);

    const results: { id: string; ok: boolean; error?: string }[] = [];

    for (const id of pendingDeleteIds) {
      try {
        const res = await fetch(apiEndpoints.salts.delete(id), {
          method: 'DELETE',
          headers: authHeaders(),
          credentials: 'include',
        });
        if (res.status === 204 || res.ok) {
          results.push({ id, ok: true });
        } else {
          const body = await res.json().catch(() => ({}));
          results.push({
            id,
            ok: false,
            error: body.error || `HTTP ${res.status}`,
          });
        }
      } catch (e) {
        results.push({
          id,
          ok: false,
          error: e instanceof Error ? e.message : 'Network error',
        });
      }
    }

    const okIds = results.filter((r) => r.ok).map((r) => r.id);
    const failed = results.filter((r) => !r.ok);

    if (okIds.length) {
      const remaining = salts.filter((s) => !okIds.includes(s.id));
      setSalts(remaining);
      const nextSelected = selectedSalts.filter((id) => !okIds.includes(id));
      syncSelection(nextSelected, remaining);
      setSnackbar(
        okIds.length === 1
          ? 'Salt removed from library'
          : `${okIds.length} salts removed from library`
      );
    }

    if (failed.length) {
      const names = failed
        .map((f) => {
          const salt = salts.find((s) => s.id === f.id);
          return `${salt?.name || f.id}: ${f.error}`;
        })
        .join('; ');
      setActionMessage(`Could not delete some salts — ${names}`);
    }

    setDeleting(false);
    setConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  const pendingNames = pendingDeleteIds
    .map((id) => salts.find((s) => s.id === id)?.name || id)
    .slice(0, 8);

  const availableElements = Array.from(
    new Set(salts.flatMap((salt) => salt.elements?.map((el) => el.element?.symbol) || []))
  )
    .filter(Boolean)
    .sort() as string[];

  const renderSolubilityCell = (salt: Salt) => {
    const form = detectSaltPhysicalForm(salt);
    if (form === 'liquid') {
      return (
        <Tooltip title="Solubility (g/L) is for powders. Liquids are already in solution.">
          <span>N/A</span>
        </Tooltip>
      );
    }
    const value = resolveSolubility(salt.name, salt.formula, salt.solubility);
    return (
      <Tooltip
        title={
          value != null
            ? `Solubility: ${value} g/L at ~20°C`
            : 'No solubility value for this powder'
        }
      >
        <span>{formatSolubility(value)}</span>
      </Tooltip>
    );
  };

  const renderDensityCell = (salt: Salt) => {
    const form = detectSaltPhysicalForm(salt);
    if (form === 'powder') {
      return (
        <Tooltip title="Density applies to liquid fertilizers only. Powders show N/A.">
          <span>N/A</span>
        </Tooltip>
      );
    }
    const value = resolveDensity(salt.name, salt.formula, salt.density);
    return (
      <Tooltip title={value != null ? `Liquid density: ${value} g/mL` : 'Density not set'}>
        <span>{formatDensity(value)}</span>
      </Tooltip>
    );
  };

  const renderTypeCell = (salt: Salt) => {
    const form = detectSaltPhysicalForm(salt);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Chip
          label={physicalFormLabel(form)}
          size="small"
          color={form === 'liquid' ? 'info' : 'default'}
          variant={form === 'liquid' ? 'filled' : 'outlined'}
        />
        {salt.isCustom && (
          <Chip label="Custom" size="small" color="secondary" variant="outlined" />
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading salt library...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button onClick={fetchSalts} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h6">
            Salt Library ({filteredSalts.length} salts)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title="Refresh library">
              <IconButton onClick={fetchSalts}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweepIcon />}
              disabled={selectedSalts.length === 0 || deleting}
              onClick={handleDeleteSelected}
            >
              Delete selected ({selectedSalts.length})
            </Button>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showIncomplete}
                  onChange={(e) => setShowIncomplete(e.target.checked)}
                />
              }
              label="Show incomplete (no %)"
            />
            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<FilterIcon />}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search salts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Group</InputLabel>
              <Select
                value={categoryFilter}
                label="Group"
                onChange={(e) =>
                  setCategoryFilter(e.target.value as SaltCategory | 'all')
                }
              >
                <MenuItem value="all">All groups</MenuItem>
                {SALT_CATEGORY_ORDER.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Element</InputLabel>
              <Select
                value={elementFilter}
                label="Element"
                onChange={(e) => setElementFilter(e.target.value)}
              >
                <MenuItem value="">All Elements</MenuItem>
                {availableElements.map((element) => (
                  <MenuItem key={element} value={element}>
                    {element}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Form</InputLabel>
              <Select
                value={formFilter}
                label="Form"
                onChange={(e) =>
                  setFormFilter(e.target.value as 'all' | SaltPhysicalForm)
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="powder">Powder</MenuItem>
                <MenuItem value="liquid">Liquid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={customFilter}
                label="Source"
                onChange={(e) => setCustomFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Manage raw materials:</strong> trash icon or <strong>Delete selected</strong>{' '}
            removes a salt you do not stock (sign-in required).
            <br />
            <strong>Checkbox</strong> = use in this workflow. Prefer rows that show element %
            (e.g. Zinc Sulfate Monohydrate with Zn/S). Empty “Not entered” twins are{' '}
            <em>hidden by default</em> — enable “Show incomplete” to see/delete them.
            <br />
            Incomplete salts cannot be selected for calculation (they break coverage analysis).
          </Typography>
        </Alert>

        {actionMessage && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
            {actionMessage}
          </Alert>
        )}

        {selectedSalts.length > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{selectedSalts.length} selected for this workflow</strong> — they will
              be available for formulation calculations. Use “Delete selected” only if you
              want them removed from the library entirely.
            </Typography>
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      selectedSalts.length === filteredSalts.length &&
                      filteredSalts.length > 0
                    }
                    indeterminate={
                      selectedSalts.length > 0 &&
                      selectedSalts.length < filteredSalts.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Salt Name</TableCell>
                <TableCell>Formula</TableCell>
                <TableCell>Elements</TableCell>
                <TableCell>Solubility (g/L)</TableCell>
                <TableCell>Density</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map((group) => (
                <React.Fragment key={group.category}>
                  <TableRow
                    sx={{
                      backgroundColor: 'action.hover',
                      '& td': { fontWeight: 700, py: 1.25 },
                    }}
                  >
                    <TableCell colSpan={8}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        <Box>
                          {group.label}
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1, fontWeight: 400 }}
                          >
                            ({group.salts.length})
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            handleSelectGroup(group.salts.map((s) => s.id))
                          }
                          sx={{ textTransform: 'none', borderRadius: 2 }}
                        >
                          Add group to selection
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {group.salts.map((salt) => (
                    <TableRow
                      key={salt.id}
                      hover
                      selected={selectedSalts.includes(salt.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSalts.includes(salt.id)}
                          onChange={() => handleSaltToggle(salt.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {salt.name}
                        </Typography>
                        {!saltHasComposition(salt) && (
                          <Chip
                            size="small"
                            color="warning"
                            label="Incomplete — no %"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {salt.formula || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(salt.elements || []).map((element, index) => (
                            <Chip
                              key={element.id || index}
                              label={`${element.element?.symbol ?? '?'}: ${element.percentage}%`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                          {(!salt.elements || salt.elements.length === 0) && (
                            <Typography variant="caption" color="text.secondary">
                              Not entered
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{renderSolubilityCell(salt)}</TableCell>
                      <TableCell>{renderDensityCell(salt)}</TableCell>
                      <TableCell>{renderTypeCell(salt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove from library (availability)">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={deleting}
                              onClick={() => handleDeleteOne(salt.id)}
                              aria-label={`Delete ${salt.name}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredSalts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              No salts found matching the current filters.
            </Typography>
          </Box>
        )}

        {/* Confirm delete */}
        <Dialog open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}>
          <DialogTitle>
            {pendingDeleteIds.length === 1
              ? 'Remove salt from library?'
              : `Remove ${pendingDeleteIds.length} salts from library?`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This permanently removes the raw material from your salt library (not only
              from this workflow). You can add custom salts again later if needed.
            </DialogContentText>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              {pendingNames.map((n) => (
                <li key={n}>
                  <Typography variant="body2">{n}</Typography>
                </li>
              ))}
              {pendingDeleteIds.length > 8 && (
                <Typography variant="caption" color="text.secondary">
                  …and {pendingDeleteIds.length - 8} more
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={executeDelete}
              disabled={deleting}
              startIcon={
                deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
              }
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!snackbar}
          autoHideDuration={4000}
          onClose={() => setSnackbar('')}
          message={snackbar}
        />
      </CardContent>
    </Card>
  );
};

export default SaltLibraryReview;
